import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { createReviewByLinkApi, getReviewLinkApi } from "../../api/api";
import { ResultShell } from "../cumpleanos/BirthdaySteps";
import { RatingInput } from "./components/RatingStars";
import { REVIEW_COMMENT_MAX } from "./components/reviewsModel";
import "./reservas.css";
import "./components/reviews.css";
import "../cumpleanos/cumpleanos.css";

const Row = ({ label, value }) =>
  value ? (
    <div className="bday-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  ) : null;

const Shell = ({ children }) => <div className="reservas-root bday">{children}</div>;

/** Public page of the link sent after a turno (/reservas/resenar/:token). Works without login. */
const ReviewByLink = () => {
  const { token } = useParams();
  // status: loading | ready | invalid | error | already | done
  const [state, setState] = useState({ status: "loading", view: null });
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    getReviewLinkApi(token)
      .then((res) => {
        if (!active) return;
        // The back wraps the body in its usual { isSuccess, status, info } envelope.
        const view = res?.data?.info;
        if (!view || !view.venue) setState({ status: "invalid", view: null });
        else setState({ status: view.alreadyReviewed ? "already" : "ready", view });
      })
      .catch((err) => {
        if (!active) return;
        // A bad, expired or unknown token look the same on purpose.
        setState({ status: err?.response?.status === 404 ? "invalid" : "error", view: null });
      });
    return () => {
      active = false;
    };
  }, [token, reloadKey]);

  const reload = useCallback(() => {
    setState({ status: "loading", view: null });
    setReloadKey((k) => k + 1);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (!rating || submitting) return;
    setSubmitting(true);
    setFormError("");
    try {
      await createReviewByLinkApi(token, { rating, comment: comment.trim() });
      setState((s) => ({ ...s, status: "done" }));
    } catch (err) {
      const code = err?.response?.status;
      if (code === 409) setState((s) => ({ ...s, status: "already" }));
      else if (code === 404) setState({ status: "invalid", view: null });
      else setFormError(err?.response?.data?.message || "No pudimos enviar tu opinión. Probá de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.status === "loading") {
    return (
      <Shell>
        <div className="bday-state">
          <Spinner animation="border" />
        </div>
      </Shell>
    );
  }

  if (state.status === "invalid") {
    return (
      <Shell>
        <ResultShell icon="search" tone="neutral" title="Link inválido o vencido">
          <p>Revisá que lo hayas copiado completo desde el mensaje.</p>
        </ResultShell>
      </Shell>
    );
  }

  if (state.status === "error") {
    return (
      <Shell>
        <ResultShell icon="alert" tone="danger" title="No pudimos cargar tu turno">
          <p>Probá de nuevo en unos segundos.</p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-primary" onClick={reload}>
              Reintentar
            </button>
          </div>
        </ResultShell>
      </Shell>
    );
  }

  if (state.status === "already") {
    return (
      <Shell>
        <ResultShell icon="check" title="Ya dejaste tu opinión, ¡gracias!" />
      </Shell>
    );
  }

  if (state.status === "done") {
    return (
      <Shell>
        <ResultShell icon="check" title="¡Gracias por tu opinión!">
          <p>Tu reseña ya está publicada y ayuda a otras personas a elegir.</p>
        </ResultShell>
      </Shell>
    );
  }

  const { venue, reservation = {} } = state.view;
  const services = Array.isArray(reservation.services) ? reservation.services.filter(Boolean).join(" + ") : "";
  const when = [reservation.dateLabel, reservation.timeLabel].filter(Boolean).join(" · ");

  return (
    <Shell>
      <ResultShell icon="cal" tone="neutral" title="¿Cómo fue tu experiencia?">
        <div className="bday-summary">
          <Row label="Lugar" value={venue.name} />
          <Row label="Turno" value={when} />
          <Row label="Servicios" value={services} />
          <Row label="Profesional" value={reservation.professionalName} />
        </div>
        <form onSubmit={submit} style={{ width: "100%", textAlign: "left" }}>
          <RatingInput value={rating} onChange={(n) => { setRating(n); setFormError(""); }} disabled={submitting} />
          <label className="rv-form-label" htmlFor="rv-link-comment">Comentario (opcional)</label>
          <textarea
            id="rv-link-comment"
            className="rv-textarea"
            value={comment}
            maxLength={REVIEW_COMMENT_MAX}
            disabled={submitting}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Contá qué te pareció"
          />
          <div className="rv-counter" aria-live="polite">{comment.length}/{REVIEW_COMMENT_MAX}</div>
          {formError && <div className="rv-form-error" role="alert">{formError}</div>}
          <div className="bday-state-actions">
            <button type="submit" className="r-btn-primary" disabled={!rating || submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : "Enviar opinión"}
            </button>
          </div>
        </form>
      </ResultShell>
    </Shell>
  );
};

export default ReviewByLink;
